'use client'

import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { trackEvent } from '@/lib/analytics/posthog'
import { recordMemberActivity } from '@/lib/analytics/memberActivity'

/** The vendor slug of a /portfolio/<vendor>[/<product>] page, else null. */
export function vendorSlugFromPath(pathname: string | null | undefined): string | null {
  const m = (pathname || '').match(/(?:^|\/)portfolio\/([^/?#]+)/)
  return m?.[1] || null
}

type Props = {
  href: string
  contactName?: string | null
  contactRole?: string | null
  ariaLabel: string
  className?: string
  children: React.ReactNode
}

// Client wrapper for the vendor-contact LinkedIn avatar link so the click is
// captured in PostHog. This click is a direct lead signal (a reader leaving a
// vendor page for the vendor contact's LinkedIn) and feeds the quarterly
// vendor reports; the rest of the card stays server-rendered.
export const TrackedLinkedinLink: React.FC<Props> = ({
  href,
  contactName,
  contactRole,
  ariaLabel,
  className,
  children,
}) => {
  const pathname = usePathname()
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className={className}
      onClick={() => {
        trackEvent('vendor_contact_clicked', {
          target: 'linkedin',
          target_url: href,
          contact_name: contactName ?? undefined,
          contact_role: contactRole ?? undefined,
          page_path: pathname,
        })
        // Signed-in members only (EA's own record, member_activity): the vendor (else the
        // channel), never the contact person.
        void recordMemberActivity('vendor_contact', {
          target: vendorSlugFromPath(pathname) ?? 'linkedin',
        })
      }}
    >
      {children}
    </Link>
  )
}
